import { UI_PRIMITIVE__button } from '@/components/ui/UI_PRIMITIVE__button';
import { CONSTANTS__client } from '@/constants/CONSTANTS__client';
import { use_dispatch } from '@/hooks/_base/use_dispatch';
import { use_component_is_mounted_0 } from '@/hooks/is_mounted/use_component_is_mounted_0';
import { use_slice_images, use_slice_user, use_slice_workspace } from '@/hooks/store/use_slices';
import { useToast } from '@/hooks/use-toast';
import i18n_base_ui from '@/locales/keys/i18n_base_ui.json';
import { HOOK_GRID2D_CELL__button_size } from '@/pages/_base/grid2d_cell/_hooks/HOOK_GRID2D_CELL__button_size';
import { ENUM__i18n_namespace } from '@/services/i18n/ENUM__i18n_namespace';
import { remote_images_ai_description_create } from '@/state/images/remote_images_ai_description_create';
import { show_toast_ai_description } from '@/utils/UTILS__generation';
import { CONSTANTS__shared } from '@shared/constants/CONSTANTS__shared';
import { RequestImageAiDescriptionCreateOrTranslate } from '@shared/dto/+image/image_ai_description/_requests/request_image_ai_description_create_or_translate';
import { DTOImageAiDescription } from '@shared/dto/+image/image_ai_description/dto_image_ai_description';
import { DTO__image_asset } from '@shared/dto/+image/image_asset_base/DTO__image_asset';
import { DTO__entity_with_grid2d } from '@shared/dto/+workspace/workspace_entity/_database_entity_with_grid2d_permissions/DTO__entity_with_grid2d';
import { ENUM__workspace_entity_feature } from '@shared/dto/+workspace/workspace_entity/_enums/ENUM__workspace_entity_feature';
import { ENUM__language_type } from '@shared/enums/ENUM__language_type';
import { DTO_RESPONSE__error } from '@shared/network/DTO_RESPONSE__error';
import { is_array_of } from '@shared/utils/utils_array';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UI_PRIMITIVE__progress_bar } from '../../../../components/ui/UI_PRIMITIVE__progress_bar';

interface Props {
  image: DTO__image_asset;
  entity: DTO__entity_with_grid2d;
  wrapper_class_name: string;
}

export function ButtonGrid2dImageImageToTextCreate({ image, entity, wrapper_class_name }: Props) {
  const { t } = useTranslation(ENUM__i18n_namespace.BASE_UI);

  const dispatch = use_dispatch();
  const { toast, dismiss } = useToast();

  const { current_workspace_user, workspace_current } = use_slice_workspace(['current_workspace_user', 'workspace_current']);
  const { user_settings } = use_slice_user(['user_settings']);
  const { ai_descriptions_map, ai_descriptions_creating_in_progress } = use_slice_images(['ai_descriptions_map', 'ai_descriptions_creating_in_progress']);

  const grid2d_cell_button_size = HOOK_GRID2D_CELL__button_size(user_settings.ui_grid2d_cell_size);

  const [isClicked, setIsClicked] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const ref_current_toast_id = useRef('');

  const ai_description = useMemo(
    () => Array.from(ai_descriptions_map.values()).find(item => item.image_id == image._id && item.language == ENUM__language_type.ENGLISH),
    [ai_descriptions_map],
  );

  const is_mounted = use_component_is_mounted_0();

  /*
   * EFFECTS
   */

  /*
   * EVENTS
   */

  function onTimerComplete() {
    setIsClicked(false);
    dismiss(ref_current_toast_id.current);
    ref_current_toast_id.current = '';
  }

  async function onClick() {
    if (ai_descriptions_creating_in_progress.has(image._id)) return;

    if (isClicked || !user_settings.confirm_action_image_to_text) {
      onTimerComplete();

      let request = new RequestImageAiDescriptionCreateOrTranslate();
      request.image_id = image._id;
      request.language = user_settings.regional_language;
      request.workspace_id = workspace_current._id;

      setIsLoading(true);
      let action_result = await dispatch(remote_images_ai_description_create(request));
      setIsLoading(false);

      await try_show_ai_description(action_result.payload);
    } else {
      setIsClicked(true);

      let text = t(i18n_base_ui.toasts.grid2d_image_to_text_create.description, { number: CONSTANTS__shared.PRICE_CREDITS_PER_IMAGE_TO_TEXT.toString() });

      let newToast = toast({
        title: t(i18n_base_ui.toasts.grid2d_image_to_text_create.title),
        duration: CONSTANTS__client.TIMEOUT_PURCHASE,
        description: <UI_PRIMITIVE__progress_bar timeout_ms={CONSTANTS__client.TIMEOUT_PURCHASE} {...{ onTimerComplete, text }} />,
      });

      ref_current_toast_id.current = newToast.id;
    }
  }

  async function try_show_ai_description(response: DTOImageAiDescription[] | DTO_RESPONSE__error | string | undefined) {
    if (!is_array_of(response, DTOImageAiDescription)) return;

    let target_language = user_settings.regional_language;

    if (user_settings.regional_language == ENUM__language_type.ENGLISH || user_settings.regional_use_english_tokens_in_prompt) {
      //show english
      target_language = ENUM__language_type.ENGLISH;
    }

    let ai_description = response.find_or_assert(item => item.language == target_language, { context: 'ai_description', target_language });

    await show_toast_ai_description(toast, ai_description);
  }

  function is_disabled(): boolean {
    return ai_descriptions_creating_in_progress.has(image._id) || isLoading;
  }

  /*
   * RENDERS
   */

  function render_title(): string {
    if (ai_descriptions_creating_in_progress.has(image._id)) return 'ANIM'; //todo baz show animation

    return 'TXT';
  }

  /*
   * HTML
   */

  if (!current_workspace_user.is_feature_pertmitted(ENUM__workspace_entity_feature.IMAGE_GET_AI_DESCRIPTION)) return null; //action is not allowed for this user
  if (ai_description) return null; //ai description already created
  //todo:check is user guest

  return (
    <div className={wrapper_class_name}>
      <UI_PRIMITIVE__button
        disabled={is_disabled()}
        variant="no-border-on-component"
        onClick={onClick}
        className={`cursor-pointer rounded-lg p-0   font-bold !text-purchase border-[1px] !border-purchase bg-bar
          ${grid2d_cell_button_size}
          ${is_mounted ? `opacity-100` : 'opacity-0'}
          `}
      >
        {render_title()}
      </UI_PRIMITIVE__button>
    </div>
  );
}
